// AcceptAlarmModal.js — Accept alarm confirmation modal (PRD-48)
// Standalone: usable from both DriftAlarmDrawer and AssetDataSections.
//
// PRD-63 WP3: dual-route on submit. When an alarmId is present (above-threshold
// finding with an alarm row), use the alarm-disposition endpoint. When the
// caller passes a `finding` without an alarmId (below-threshold finding —
// severity below asset.alarmSeverityThreshold means no alarm row exists), fall
// back to the generic /suppression-create endpoint. Both paths return an
// assetRisk payload in their response, so onSuccess(result) bubbles the same
// optimistic risk-card refresh contract regardless of which path ran.

import React, { useState } from 'react';
import { acceptAlarm } from '../services/driftService';
import { createSuppression } from '../services/suppressionService';

const DURATION_OPTIONS = [
    { label: '1 month', days: 30 },
    { label: '3 months', days: 90 },
    { label: '6 months', days: 180 },
    { label: '12 months', days: 365 },
];

function AcceptAlarmModal({ isOpen, onClose, alarm, finding, assetId, onSuccess, defaultScope = 'host' }) {
    const [reason, setReason] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(90);
    const [scope, setScope] = useState(defaultScope);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const alarmId = alarm?.alarmId || finding?.alarmId;
    const displayName = alarm?.name || finding?.name || finding?.findingName || 'this alarm';

    const handleSubmit = async () => {
        if (reason.trim().length < 10) {
            setError('Reason must be at least 10 characters.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            let result;
            if (alarmId) {
                result = await acceptAlarm(alarmId, {
                    reason: reason.trim(),
                    expiresInDays,
                    scope,
                });
            } else {
                // Below-threshold finding — no alarm row. Use the generic
                // suppression-create path; it also recomputes asset risk
                // internally and returns assetRisk in the response.
                const expiresAt = new Date(Date.now() + expiresInDays * 86400000).toISOString();
                result = await createSuppression({
                    assetId: assetId || finding?.assetId,
                    findingType: finding?.findingType || 'vulnerability',
                    findingIdentifier: finding?.findingIdentifier || finding?.templateId
                        || finding?.cve || finding?.cveId || finding?.id || finding?.name,
                    findingName: finding?.findingName || finding?.name || finding?.title || 'Unknown',
                    findingId: finding?.findingId || null,
                    matchedAt: finding?.matchedAt || finding?.host || finding?.url || null,
                    severity: finding?.severity || null,
                    reason: reason.trim(),
                    disposition: 'accept',
                    expiresAt,
                    scope,
                });
            }
            onSuccess && onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to accept alarm');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="alarm-modal-backdrop" onClick={onClose}>
            <div className="alarm-modal" onClick={e => e.stopPropagation()}>
                <div className="alarm-modal__header">
                    <h3>Accept Alarm</h3>
                    <button className="alarm-modal__close" onClick={onClose}>×</button>
                </div>
                <div className="alarm-modal__body">
                    <p className="alarm-modal__desc">
                        Accepting <strong>{displayName}</strong> marks it as a known, acceptable risk.
                        The finding stays in your risk score, but the alarm will not fire again until the suppression expires.
                    </p>

                    <div className="alarm-modal__field">
                        <label>Duration</label>
                        <div className="alarm-modal__pills">
                            {DURATION_OPTIONS.map(opt => (
                                <button
                                    key={opt.days}
                                    className={`alarm-modal__pill ${expiresInDays === opt.days ? 'active' : ''}`}
                                    onClick={() => setExpiresInDays(opt.days)}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="alarm-modal__field">
                        <label>Scope</label>
                        <div className="alarm-modal__pills">
                            <button
                                className={`alarm-modal__pill ${scope === 'host' ? 'active' : ''}`}
                                onClick={() => setScope('host')}
                                type="button"
                            >
                                This detection only
                            </button>
                            <button
                                className={`alarm-modal__pill ${scope === 'asset' ? 'active' : ''}`}
                                onClick={() => setScope('asset')}
                                type="button"
                            >
                                All occurrences on this asset
                            </button>
                        </div>
                        <p className="alarm-modal__scope-hint">
                            {scope === 'host'
                                ? `Only suppresses ${alarm?.matchedAt || finding?.matchedAt || 'this exact match'}. New findings of the same type elsewhere on this asset will still alarm.`
                                : `Future findings of "${displayName}" on this asset will be auto-accepted until the suppression expires.`}
                        </p>
                    </div>

                    <div className="alarm-modal__field">
                        <label>Reason <span className="alarm-modal__hint">(min 10 chars)</span></label>
                        <textarea
                            className="alarm-modal__textarea"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g., Known SSH bastion, tracked in Jira INFRA-1234"
                            rows={3}
                        />
                    </div>

                    {error && <div className="alarm-modal__error">{error}</div>}
                </div>
                <div className="alarm-modal__footer">
                    {reason.trim().length < 10 && reason.trim().length > 0 && (
                        <span className="alarm-modal__footer-hint">
                            {10 - reason.trim().length} more character{10 - reason.trim().length !== 1 ? 's' : ''} needed
                        </span>
                    )}
                    <button className="alarm-modal__btn alarm-modal__btn--cancel" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="alarm-modal__btn alarm-modal__btn--accept"
                        onClick={reason.trim().length < 10 ? () => setError('Please enter a reason (at least 10 characters) to accept this alarm.') : handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Accepting...' : 'Accept'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AcceptAlarmModal;
